import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPayrollEmployees, getSalarySlips, getMySlips, createSalarySlip, runPayroll,
  updateSlip, getSlip, issueSlip, issueAllSlips, voidSlip, markSlipPaid,
  deleteSlip, downloadSlipPdf, getCurrencies,
  getSalaryStructure, upsertSalaryStructure, getSalaryStructureHistory,
  getWorkedDays, estimateTax,
  getPayrollSettings, updatePayrollSettings,
  downloadPnd1Report, downloadSso10Report, downloadAnnualSummary,
  downloadBankFile, downloadForm16, downloadWhtCertificate, getPtStates,
} from "../api";
import { useToast } from "../context/ToastContext";
import { SkeletonRow } from "../components/Skeleton";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { useRouteTab } from "../hooks/useRouteTab";

interface SalarySlipsPageProps { employeeOnly?: boolean }

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const SLIP_TYPES = ["regular","supplementary","bonus","correction"];
const curYear = new Date().getFullYear();

// ── Styles ─────────────────────────────────────────────────────────────────
const primaryBtn: React.CSSProperties = {
  background: "linear-gradient(135deg, #1e3a8a, #00c9a7)",
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtn: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const inputSt: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
const labelSt: React.CSSProperties = { display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569" };
const menuItem: React.CSSProperties = {
  display: "block", width: "100%", textAlign: "left", padding: "8px 16px",
  background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#1e293b", whiteSpace: "nowrap",
};

// ── Status badge ────────────────────────────────────────────────────────────
function SlipBadge({ status }: { status: string }) {
  const map: Record<string, [string, string, string]> = {
    issued:  ["#dcfce7","#166534","#86efac"],
    draft:   ["#fef9c3","#854d0e","#fde047"],
    voided:  ["#f1f5f9","#94a3b8","#cbd5e1"],
  };
  const [bg, color, border] = map[status] ?? ["#f1f5f9","#64748b","#e2e8f0"];
  return (
    <span style={{ background: bg, color, border: `1px solid ${border}`, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
      {status}
    </span>
  );
}

function PayBadge({ status }: { status: string }) {
  if (status === "paid") return <span style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>✓ paid</span>;
  return <span style={{ fontSize: 11, color: "#94a3b8" }}>unpaid</span>;
}

function TypeChip({ type }: { type: string }) {
  if (!type || type === "regular") return null;
  return (
    <span style={{ background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd", padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600, marginLeft: 6 }}>
      {type}
    </span>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function SalarySlipsPage({ employeeOnly = false }: SalarySlipsPageProps) {
  const tz = useTenantTimezone();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();

  // Tab lives in the URL (/payroll, /payroll/structure, …) so the module
  // sidebar drives it (spec module-subpage-navs). Harmless under /my-slips:
  // employeeOnly returns before any tab content renders.
  const [activeTab, setActiveTab] = useRouteTab<"slips"|"structure"|"settings"|"exports">(
    "/payroll",
    { slips: "", structure: "structure", settings: "settings", exports: "exports" },
    "slips",
  );
  // Dashboard deep links: /payroll?filter=unpaid (issued slips awaiting
  // payment) still works; legacy /payroll?tab=… links redirect to their
  // path equivalents.
  const [searchParams] = useSearchParams();
  const [unpaidOnly, setUnpaidOnly] = useState(() => searchParams.get("filter") === "unpaid");
  const navigateLegacyTab = useNavigate();
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "structure" || t === "settings" || t === "exports") {
      navigateLegacyTab(`/payroll/${t}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [reportBusy, setReportBusy] = useState<string | null>(null);
  const [reportMonth, setReportMonth] = useState(String(new Date().getMonth() + 1));
  const [reportYear, setReportYear] = useState(String(curYear));
  const [reportEmp, setReportEmp] = useState<number | null>(null);
  const [reportFyYear, setReportFyYear] = useState(String(curYear - 1));
  const [bankFormat, setBankFormat] = useState("TH");
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showRunForm, setShowRunForm] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ field: string; dir: "asc"|"desc" }>({ field: "year_month", dir: "desc" });
  const [editingSlip, setEditingSlip] = useState<any | null>(null);
  const [runResult, setRunResult] = useState<any | null>(null);
  const [showPayModal, setShowPayModal] = useState<any | null>(null);
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().slice(0,10), payment_reference: "" });
  const [structEmp, setStructEmp] = useState<number | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Run payroll options
  const [runMonth, setRunMonth] = useState(String(new Date().getMonth() + 1));
  const [runYear, setRunYear] = useState(String(curYear));
  const [runOptions, setRunOptions] = useState({ apply_statutory: false, apply_lop: false, apply_prorate: true });

  const blankForm = () => ({
    user_id: "", month: String(new Date().getMonth() + 1), year: String(curYear),
    currency: "", currency_symbol: "", basic_salary: "", worked_days: "",
    slip_type: "regular", auto_prorate: false, apply_statutory: false, apply_tax: false,
    components: [] as { name: string; type: "earning"|"deduction"; amount: string }[],
  });
  const [form, setForm] = useState(blankForm());

  const [structForm, setStructForm] = useState({
    basic_salary: "", currency: "", currency_symbol: "", effective_date: "", working_days: "",
    components: [] as { name: string; type: "earning"|"deduction"; amount: string }[],
  });
  const [structTriedSubmit, setStructTriedSubmit] = useState(false);
  const [settingsForm, setSettingsForm] = useState<any>({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [calcWorkedDays, setCalcWorkedDays] = useState(false);
  const [estimatingTax, setEstimatingTax] = useState(false);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: employees = [] } = useQuery({ queryKey: ["payroll-employees"], queryFn: getPayrollEmployees, enabled: !employeeOnly });
  const { data: currencies = [] } = useQuery({ queryKey: ["currencies"], queryFn: getCurrencies, enabled: !employeeOnly });
  const { data: slips = [], isLoading } = useQuery({
    queryKey: employeeOnly ? ["my-slips"] : ["salary-slips", selectedEmployee],
    queryFn: employeeOnly ? getMySlips : () => getSalarySlips(selectedEmployee ? { user_id: selectedEmployee } : undefined),
  });
  const { data: paySettings } = useQuery({
    queryKey: ["payroll-settings"],
    queryFn: getPayrollSettings,
    enabled: !employeeOnly,
  });
  const { data: ptStates = [] } = useQuery({
    queryKey: ["pt-states"],
    queryFn: getPtStates,
    enabled: !employeeOnly,
  });
  useEffect(() => {
    if (paySettings && !settingsLoaded) { setSettingsForm(paySettings); setSettingsLoaded(true); }
  }, [paySettings, settingsLoaded]);

  const { data: salaryStruct } = useQuery({
    queryKey: ["salary-struct", structEmp],
    queryFn: () => getSalaryStructure(structEmp!),
    enabled: !!structEmp && !employeeOnly,
  });
  const { data: structHistory = [] } = useQuery({
    queryKey: ["salary-struct-history", structEmp],
    queryFn: () => getSalaryStructureHistory(structEmp!),
    enabled: !!structEmp && !employeeOnly,
  });
  useEffect(() => {
    setStructTriedSubmit(false);
    if (salaryStruct) {
      setStructForm({
        basic_salary: String((salaryStruct as any).basic_salary ?? ""),
        currency: (salaryStruct as any).currency || "",
        currency_symbol: (salaryStruct as any).currency_symbol || "",
        effective_date: (salaryStruct as any).effective_date || "",
        working_days: (salaryStruct as any).working_days != null ? String((salaryStruct as any).working_days) : "",
        components: ((salaryStruct as any).components || []).map((c: any) => ({ ...c, amount: String(c.amount) })),
      });
    } else if (structEmp) {
      setStructForm({ basic_salary: "", currency: "", currency_symbol: "", effective_date: "", working_days: "", components: [] });
    }
  }, [salaryStruct, structEmp]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: createSalarySlip,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); setShowForm(false); setForm(blankForm()); toast("Salary slip created"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to create slip", "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateSlip(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); setShowForm(false); setEditingSlip(null); setForm(blankForm()); toast("Slip updated"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to update slip", "error"),
  });
  const issueMut = useMutation({
    mutationFn: issueSlip,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); toast("Slip issued to employee"); },
  });
  const issueAllMut = useMutation({
    mutationFn: ({ month, year }: { month: number; year: number }) => issueAllSlips(month, year),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["salary-slips"] });
      // The "Payroll run complete" banner (and its own "Issue All X Slips" button)
      // is driven by this local state, not by the query above — without clearing
      // it, the banner sat on screen unchanged after a successful issue, looking
      // like nothing happened (bug-22/91).
      setRunResult(null);
      toast(`Issued ${d.issued?.length ?? 0} slips`);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to issue all", "error"),
  });
  const voidMut = useMutation({
    mutationFn: voidSlip,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); toast("Slip voided"); },
  });
  const deleteMut = useMutation({
    mutationFn: deleteSlip,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); toast("Slip deleted", "info"); },
  });
  const markPaidMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => markSlipPaid(id, data as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); setShowPayModal(null); toast("Marked as paid"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
  });
  const runMut = useMutation({
    mutationFn: runPayroll,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["salary-slips"] }); setRunResult(d); setShowRunForm(false); toast(`${d.created?.length ?? 0} slips generated`); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Payroll run failed", "error"),
  });
  const structMut = useMutation({
    mutationFn: ({ uid, data }: { uid: number; data: object }) => upsertSalaryStructure(uid, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["salary-struct", structEmp] }); qc.invalidateQueries({ queryKey: ["salary-struct-history", structEmp] }); qc.invalidateQueries({ queryKey: ["payroll-employees"] }); toast("Salary structure saved"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save", "error"),
  });
  const settingsMut = useMutation({
    mutationFn: updatePayrollSettings,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payroll-settings"] }); toast("Payroll settings saved"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed", "error"),
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleDownload = async (slip: any) => {
    setDownloading(slip.id);
    try {
      const { blob, filename } = await downloadSlipPdf(slip.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to download PDF", "error"); }
    finally { setDownloading(null); }
  };

  const startEdit = async (slip: any) => {
    const full = await getSlip(slip.id);
    setForm({
      user_id: String(full.user_id || ""), month: String(full.month || new Date().getMonth() + 1),
      year: String(full.year || curYear), currency: full.currency || "",
      currency_symbol: full.currency_symbol || "", basic_salary: full.basic_salary != null ? String(full.basic_salary) : "",
      worked_days: full.worked_days != null ? String(full.worked_days) : "",
      slip_type: full.slip_type || "regular", auto_prorate: false, apply_statutory: false, apply_tax: false,
      components: (full.components || []).map((c: any) => ({ name: String(c.name||""), type: c.type === "deduction" ? "deduction" : "earning", amount: String(c.amount||"") })),
    });
    setEditingSlip(full); setShowForm(true); setShowRunForm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAutoWorkedDays = async () => {
    if (!form.user_id || !form.month || !form.year) return;
    setCalcWorkedDays(true);
    try {
      const wd = await getWorkedDays(parseInt(form.user_id), parseInt(form.month), parseInt(form.year));
      setForm(f => ({ ...f, worked_days: String(wd.worked_days) }));
      toast(`Worked days: ${wd.worked_days} (LWP: ${wd.lwp_days})`);
    } catch { toast("Could not calculate worked days", "error"); }
    setCalcWorkedDays(false);
  };

  const handleEstimateTax = async () => {
    if (!form.user_id) return;
    setEstimatingTax(true);
    try {
      const t = await estimateTax(parseInt(form.user_id), parseInt(form.month), parseInt(form.year));
      toast(`Estimated monthly tax: ${t.monthly_tax.toFixed(2)}`);
      // Inject tax component
      setForm(f => ({
        ...f,
        apply_tax: true,
        components: [...f.components.filter(c => c.name !== "Income Tax"),
          { name: "Income Tax", type: "deduction", amount: String(t.monthly_tax.toFixed(2)) }],
      }));
    } catch { toast("Tax estimate unavailable", "error"); }
    setEstimatingTax(false);
  };

  const onCurrencyChange = (code: string) => {
    const found = (currencies as any[]).find((c: any) => c.code === code);
    setForm(f => ({ ...f, currency: code, currency_symbol: found?.symbol || "" }));
  };

  const addComp = () => setForm(f => ({ ...f, components: [...f.components, { name: "", type: "earning", amount: "" }] }));
  const updComp = (i: number, field: string, val: string) => setForm(f => { const c=[...f.components]; c[i]={...c[i],[field]:val}; return {...f,components:c}; });
  const remComp = (i: number) => setForm(f => ({ ...f, components: f.components.filter((_,idx)=>idx!==i) }));

  const calcGross = () => (parseFloat(form.basic_salary)||0) + form.components.filter(c=>c.type==="earning").reduce((s,c)=>s+(parseFloat(c.amount)||0),0);
  const calcDed   = () => form.components.filter(c=>c.type==="deduction").reduce((s,c)=>s+(parseFloat(c.amount)||0),0);
  const calcNet   = () => calcGross()-calcDed();

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      currency: form.currency||undefined, currency_symbol: form.currency_symbol||undefined,
      basic_salary: parseFloat(form.basic_salary)||0,
      worked_days: form.worked_days ? parseInt(form.worked_days) : undefined,
      slip_type: form.slip_type,
      auto_prorate: form.auto_prorate,
      apply_statutory: form.apply_statutory,
      apply_tax: form.apply_tax,
      components: form.components.map(c=>({...c,amount:parseFloat(c.amount)||0})),
    };
    if (editingSlip) { updateMut.mutate({ id: editingSlip.id, data: payload }); }
    else { createMut.mutate({ ...payload, user_id: parseInt(form.user_id), month: parseInt(form.month), year: parseInt(form.year) }); }
  };

  // Struct form helpers
  const addStructComp = () => setStructForm(f=>({...f,components:[...f.components,{name:"",type:"earning",amount:""}]}));
  const updStructComp = (i: number, field: string, val: string) => setStructForm(f=>{const c=[...f.components];c[i]={...c[i],[field]:val};return{...f,components:c};});
  const remStructComp = (i: number) => setStructForm(f=>({...f,components:f.components.filter((_,idx)=>idx!==i)}));

  const structErrors = (() => {
    const errs: { basic_salary?: string; components?: string } = {};
    const basic = parseFloat(structForm.basic_salary);
    if (!structForm.basic_salary || isNaN(basic) || basic <= 0) errs.basic_salary = "Base salary must be greater than 0";
    if (structForm.components.some(c => parseFloat(c.amount) < 0)) errs.components = "Component amounts cannot be negative";
    return errs;
  })();

  const saveStructure = () => {
    if (!structEmp) return;
    if (Object.keys(structErrors).length > 0) { setStructTriedSubmit(true); return; }
    structMut.mutate({ uid: structEmp, data: {
      basic_salary: parseFloat(structForm.basic_salary)||0,
      components: structForm.components.map(c=>({...c,amount:parseFloat(c.amount)||0})),
      // "" means "Tenant default" — must still be sent so the backend can clear a
      // previously-set override; ||undefined would drop the key from the JSON body
      // entirely and silently leave the old currency in place (bug-25).
      currency: structForm.currency,
      currency_symbol: structForm.currency_symbol,
      effective_date: structForm.effective_date||undefined,
      working_days: structForm.working_days === "" ? "" : parseFloat(structForm.working_days)||0,
    }});
  };

  const loadStructForEmp = (uid: number) => {
    setStructEmp(uid);
    setStructForm({ basic_salary:"",currency:"",currency_symbol:"",effective_date:"",working_days:"",components:[] });
  };

  // ── Filtered/sorted slips ─────────────────────────────────────────────────
  const filteredSlips = (slips as any[])
    .map(s => ({ ...s, year_month: s.year*100+s.month }))
    .filter(s => !unpaidOnly || (s.status === "issued" && s.payment_status !== "paid"))
    .filter(s => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (s.employee_name||"").toLowerCase().includes(q)||
             (s.month_name||"").toLowerCase().includes(q)||
             String(s.year).includes(q)||
             (s.slip_type||"").includes(q);
    })
    .sort((a,b)=>{
      const av=a[sort.field]??"", bv=b[sort.field]??"";
      const cmp = typeof av==="number" ? av-bv : String(av).localeCompare(String(bv));
      return sort.dir==="asc" ? cmp : -cmp;
    });

  const draftCount = (slips as any[]).filter((s:any)=>s.status==="draft").length;

  // ── Render ────────────────────────────────────────────────────────────────
  if (employeeOnly) {
    return (
      <div>
        <LoadingOverlay active={downloading !== null} />
        <div className="page-header">
          <div>
            <h1 style={{ margin:0, color:"#0f172a", fontSize:24, fontWeight:700 }}>My Salary Slips</h1>
            <p style={{ margin:"4px 0 0", color:"#64748b", fontSize:14 }}>Your issued salary slips</p>
          </div>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by month or year…"
          style={{ ...inputSt, maxWidth:300, padding:"7px 12px", marginBottom:12 }} />
        {isLoading && <div style={{display:"grid",gap:10}}><SkeletonRow/><SkeletonRow/></div>}
        <div className="table-wrap" style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10 }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
                {["Pay Period","Net Pay","Type","Payment",""].map(h=>(
                  <th key={h} style={{ padding:"10px 16px", textAlign:h==="Net Pay"?"right":"left", fontWeight:600, color:"#475569", fontSize:12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSlips.map((s:any)=>(
                <tr key={s.id} style={{ borderBottom:"1px solid #f1f5f9" }}
                    onMouseEnter={e=>(e.currentTarget.style.background="#f8fafc")}
                    onMouseLeave={e=>(e.currentTarget.style.background="white")}>
                  <td style={{ padding:"12px 16px" }}>{s.month_name} {s.year}
                    <div style={{ fontSize:11, color:"#94a3b8" }}>{s.generated_at && formatDate(s.generated_at, "short", tz)}</div>
                  </td>
                  <td style={{ padding:"12px 16px", textAlign:"right", fontWeight:600, color:"#1e3a8a" }}>
                    {s.currency_symbol}{s.net_salary?.toLocaleString("en",{minimumFractionDigits:2})}
                    <span style={{ fontSize:11, color:"#64748b", marginLeft:4 }}>{s.currency}</span>
                  </td>
                  <td style={{ padding:"12px 16px" }}><TypeChip type={s.slip_type}/></td>
                  {/* Payment reconciliation is internal bookkeeping — surface it here
                      only once it says something true for the employee ("your salary
                      was sent"), never the unpaid default many tenants never update. */}
                  <td style={{ padding:"12px 16px" }}>
                    {s.payment_status === "paid" && (
                      <span style={{ fontSize:11, color:"#166534", fontWeight:600 }}>
                        ✓ Paid{s.payment_date ? ` on ${formatDate(s.payment_date, "short", tz)}` : ""}
                      </span>
                    )}
                  </td>
                  <td style={{ padding:"12px 16px", textAlign:"right" }}>
                    <AsyncButton onClick={()=>handleDownload(s)} style={{ ...outlineBtn, fontSize:12 }}>
                      {downloading===s.id?"…":"⬇ PDF"}
                    </AsyncButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isLoading && filteredSlips.length===0 && (
            <div style={{ textAlign:"center", padding:"60px 0", color:"#94a3b8" }}>No salary slips issued yet.</div>
          )}
        </div>
      </div>
    );
  }

  // ── HR/Admin view ─────────────────────────────────────────────────────────
  return (
    <div>
      <LoadingOverlay active={
        createMut.isPending || updateMut.isPending || runMut.isPending || structMut.isPending ||
        issueAllMut.isPending || issueMut.isPending || voidMut.isPending || deleteMut.isPending ||
        markPaidMut.isPending || settingsMut.isPending || downloading !== null
      } />
      <div className="page-header">
        <div>
          <h1 style={{ margin:0, color:"#0f172a", fontSize:24, fontWeight:700 }}>Salary Slips</h1>
          <p style={{ margin:"4px 0 0", color:"#64748b", fontSize:14 }}>Generate and manage employee salary slips</p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <button onClick={()=>exportSlipsCsv(slips as any[])} style={{ ...outlineBtn, fontSize:12, padding:"6px 12px", color:"#94a3b8" }}>↓ CSV</button>
          <button onClick={()=>{
            // The run-payroll panel only ever renders inside the Slips tab's content
            // block — opening it from any other tab (Salary Structure/Payroll
            // Settings/Exports) toggled this button to "Cancel" with nothing visibly
            // appearing anywhere (bug-27). Switch tabs so the panel is on-screen.
            setShowRunForm(f=>{ const next=!f; if(next) setActiveTab("slips"); return next; });
            setShowForm(false);setRunResult(null);
          }} style={primaryBtn}>
            {showRunForm ? "Cancel" : "▶ Run Payroll"}
          </button>
          <button onClick={()=>{
            // Same bug-27 defect as Run Payroll above, on the button beside it: the single-slip
            // form also renders only inside the Slips tab's content block, so pressing this from
            // Salary Structure / Payroll Settings / Exports flipped the label to "Cancel" and
            // showed nothing anywhere. The original fix only ever covered Run Payroll. Switch
            // tabs so the form is on-screen, exactly as that one does.
            setShowForm(f=>{ const next=!f; if(next) setActiveTab("slips"); return next; });
            setShowRunForm(false);
            if(showForm){setEditingSlip(null);setForm(blankForm());}
          }}
            style={{ ...primaryBtn, background:"white", color:"#1e3a8a", border:"1.5px solid #1e3a8a" }}>
            {showForm ? "Cancel" : "+ Single Slip"}
          </button>
        </div>
      </div>

      {/* Tab pill bar removed — the module sidebar is the switcher now
          (Payslips / Salary Structure / Payroll Settings / Exports). */}

      {/* ══ Tab: Payroll Settings ══════════════════════════════════════════════ */}
      {activeTab==="settings" && (
        <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:24 }}>
          <h3 style={{ margin:"0 0 18px", color:"#0f172a" }}>Payroll Settings</h3>
          {!paySettings ? <p style={{ color:"#94a3b8" }}>Loading…</p> : (
            <>
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16, marginBottom:20 }}>
                <div>
                  <label style={labelSt}>Country / Tax Regime</label>
                  <select value={settingsForm.country||"TH"} onChange={e=>setSettingsForm((f:any)=>({...f,country:e.target.value}))} style={inputSt}>
                    <option value="TH">Thailand (PIT)</option>
                    <option value="IN">India (TDS)</option>
                    <option value="US">US</option>
                    <option value="SG">Singapore</option>
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Working Days / Month</label>
                  <input type="number" value={settingsForm.work_days_per_month||26} style={inputSt}
                    onChange={e=>setSettingsForm((f:any)=>({...f,work_days_per_month:parseInt(e.target.value)}))} min={1} max={31}/>
                </div>
              </div>
              {/* ── Thailand ── */}
              {(settingsForm.country||"TH") === "TH" && (
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:16, marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#166534", marginBottom:12 }}>🇹🇭 Thailand</div>
                {/* SSF */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:8 }}>
                    <input type="checkbox" checked={!!settingsForm.ssf_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,ssf_enabled:e.target.checked}))}/>
                    <strong>SSF</strong> — Social Security Fund (mandatory)
                  </label>
                  {settingsForm.ssf_enabled && (
                    <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap:10, paddingLeft:22 }}>
                      <div><label style={labelSt}>Emp Rate (%)</label><input type="number" value={settingsForm.ssf_rate||5} style={inputSt} step="0.1" onChange={e=>setSettingsForm((f:any)=>({...f,ssf_rate:parseFloat(e.target.value)}))}/></div>
                      <div><label style={labelSt}>Emp Cap (฿)</label><input type="number" value={settingsForm.ssf_max_amount||750} style={inputSt} onChange={e=>setSettingsForm((f:any)=>({...f,ssf_max_amount:parseFloat(e.target.value)}))}/></div>
                      <div><label style={labelSt}>Er Rate (%)</label><input type="number" value={settingsForm.employer_ssf_rate||5} style={inputSt} step="0.1" onChange={e=>setSettingsForm((f:any)=>({...f,employer_ssf_rate:parseFloat(e.target.value)}))}/></div>
                      <div><label style={labelSt}>Er Cap (฿)</label><input type="number" value={settingsForm.employer_ssf_max||750} style={inputSt} onChange={e=>setSettingsForm((f:any)=>({...f,employer_ssf_max:parseFloat(e.target.value)}))}/></div>
                    </div>
                  )}
                </div>
                {/* PVD */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:8 }}>
                    <input type="checkbox" checked={!!settingsForm.pvd_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,pvd_enabled:e.target.checked}))}/>
                    <strong>PVD</strong> — Private Provident Fund (voluntary)
                  </label>
                  {settingsForm.pvd_enabled && (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, paddingLeft:22 }}>
                      <div><label style={labelSt}>Employee Rate (%)</label><input type="number" value={settingsForm.pvd_employee_rate||5} style={inputSt} step="0.1" onChange={e=>setSettingsForm((f:any)=>({...f,pvd_employee_rate:parseFloat(e.target.value)}))}/></div>
                      <div><label style={labelSt}>Employer Match Rate (%)</label><input type="number" value={settingsForm.pvd_employer_rate||5} style={inputSt} step="0.1" onChange={e=>setSettingsForm((f:any)=>({...f,pvd_employer_rate:parseFloat(e.target.value)}))}/></div>
                    </div>
                  )}
                </div>
                {/* Thai-specific options */}
                <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
                  <input type="checkbox" checked={!!settingsForm.be_dates_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,be_dates_enabled:e.target.checked}))}/>
                  Show Buddhist Era (B.E.) year on payslips
                </label>
              </div>
              )}

              {/* ── India ── */}
              {settingsForm.country === "IN" && (
              <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:16, marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#991b1b", marginBottom:12 }}>🇮🇳 India</div>
                {/* PF */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:8 }}>
                    <input type="checkbox" checked={!!settingsForm.pf_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,pf_enabled:e.target.checked}))}/>
                    <strong>PF / EPF</strong> — Provident Fund (mandatory)
                  </label>
                  {settingsForm.pf_enabled && (
                    <>
                      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap:10, paddingLeft:22, marginBottom:8 }}>
                        <div><label style={labelSt}>Emp Rate (%)</label><input type="number" value={settingsForm.pf_rate||12} style={inputSt} step="0.1" onChange={e=>setSettingsForm((f:any)=>({...f,pf_rate:parseFloat(e.target.value)}))}/></div>
                        <div><label style={labelSt}>Er Rate (%)</label><input type="number" value={settingsForm.employer_pf_rate||12} style={inputSt} step="0.1" onChange={e=>setSettingsForm((f:any)=>({...f,employer_pf_rate:parseFloat(e.target.value)}))}/></div>
                        <div><label style={labelSt}>Wage Ceiling (₹)</label><input type="number" value={settingsForm.pf_cap_salary||15000} style={inputSt} onChange={e=>setSettingsForm((f:any)=>({...f,pf_cap_salary:parseFloat(e.target.value)}))}/></div>
                      </div>
                      <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, cursor:"pointer", paddingLeft:22 }}>
                        <input type="checkbox" checked={!!settingsForm.pf_split_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,pf_split_enabled:e.target.checked}))}/>
                        Show EPF / EPS / EDLI breakdown on payslip
                      </label>
                    </>
                  )}
                </div>
                {/* ESI */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:8 }}>
                    <input type="checkbox" checked={!!settingsForm.esi_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,esi_enabled:e.target.checked}))}/>
                    <strong>ESI</strong> — Employee State Insurance (0.75% ee / 3.25% er)
                  </label>
                  {settingsForm.esi_enabled && (
                    <div style={{ paddingLeft:22 }}>
                      <div><label style={labelSt}>Wage Limit (₹/month, default 21000)</label><input type="number" value={settingsForm.esi_wage_limit||21000} style={{ ...inputSt, maxWidth:200 }} onChange={e=>setSettingsForm((f:any)=>({...f,esi_wage_limit:parseFloat(e.target.value)}))}/></div>
                    </div>
                  )}
                </div>
                {/* PT */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:8 }}>
                    <input type="checkbox" checked={!!settingsForm.pt_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,pt_enabled:e.target.checked}))}/>
                    <strong>Professional Tax</strong> (state-specific deduction)
                  </label>
                  {settingsForm.pt_enabled && (
                    <div style={{ paddingLeft:22 }}>
                      <label style={labelSt}>State</label>
                      <select value={settingsForm.pt_state||"KA"} style={{ ...inputSt, maxWidth:220 }} onChange={e=>setSettingsForm((f:any)=>({...f,pt_state:e.target.value}))}>
                        {(ptStates as any[]).map((s:any)=><option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                      </select>
                    </div>
                  )}
                </div>
                {/* Gratuity */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
                    <input type="checkbox" checked={!!settingsForm.gratuity_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,gratuity_enabled:e.target.checked}))}/>
                    <strong>Gratuity Provision</strong> — 4.81% of basic (employer cost, not deducted from salary)
                  </label>
                </div>
                {/* Tax regime */}
                <div>
                  <label style={labelSt}>Income Tax Regime</label>
                  <select value={settingsForm.tax_regime||"new"} style={{ ...inputSt, maxWidth:220 }} onChange={e=>setSettingsForm((f:any)=>({...f,tax_regime:e.target.value}))}>
                    <option value="new">New Regime (FY2024-25, ₹75k std deduction)</option>
                    <option value="old">Old Regime (₹50k std deduction + HRA + 80C)</option>
                  </select>
                </div>
              </div>
              )}

              {/* ── Common ── */}
              <div style={{ marginBottom:16 }}>
                <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
                  <input type="checkbox" checked={!!settingsForm.tax_auto_enabled} onChange={e=>setSettingsForm((f:any)=>({...f,tax_auto_enabled:e.target.checked}))}/>
                  Auto-compute income tax withholding on bulk payroll runs
                </label>
              </div>
              <button onClick={()=>settingsMut.mutate(settingsForm)} disabled={settingsMut.isPending} style={primaryBtn}>
                {settingsMut.isPending ? "Saving…" : "Save Settings"}
              </button>
            </>
          )}
        </div>
      )}

      {/* ══ Tab: Exports ══════════════════════════════════════════════════════ */}
      {activeTab==="exports" && (() => {
        const country = settingsForm.country || "TH";
        const isTH = country === "TH";
        const isIN = country === "IN";
        const bankFmt = isTH ? "TH" : "IN";
        return (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {/* Period selector */}
          <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:20 }}>
            <div style={{ fontWeight:700, fontSize:14, color:"#1e3a8a", marginBottom:14 }}>Export Period</div>
            <div style={{ display:"flex", gap:14, flexWrap:"wrap", alignItems:"flex-end" }}>
              <div><label style={labelSt}>Month</label>
                <select value={reportMonth} onChange={e=>setReportMonth(e.target.value)} style={{ ...inputSt, width:140 }}>
                  {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div><label style={labelSt}>Year</label>
                <input type="number" value={reportYear} onChange={e=>setReportYear(e.target.value)} style={{ ...inputSt, width:100 }} min={2020} max={2099}/>
              </div>
              <div><label style={labelSt}>Employee (for individual exports)</label>
                <select value={reportEmp||""} onChange={e=>setReportEmp(e.target.value?parseInt(e.target.value):null)} style={{ ...inputSt, width:220 }}>
                  <option value="">Select employee…</option>
                  {(employees as any[]).map((e:any)=><option key={e.id} value={e.id}>{e.name} {e.employee_id?`(${e.employee_id})`:""}</option>)}
                </select>
              </div>
              {isIN && (
              <div><label style={labelSt}>FY Start Year</label>
                <input type="number" value={reportFyYear} onChange={e=>setReportFyYear(e.target.value)} style={{ ...inputSt, width:100 }} min={2020} max={2099} title="e.g. 2025 = FY 2025-26 (April 2025 – March 2026)"/>
              </div>
              )}
            </div>
          </div>

          {/* Thailand Exports */}
          {isTH && (
          <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:20 }}>
            <div style={{ fontWeight:700, fontSize:14, color:"#166534", marginBottom:14 }}>🇹🇭 Thailand</div>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              {[
                { key:"pnd1", label:"PND.1 Monthly WHT", desc:"Revenue Dept monthly withholding tax", fn:()=>downloadPnd1Report(parseInt(reportMonth),parseInt(reportYear)) },
                { key:"sso10", label:"SSO.10 SSF Contribution", desc:"Monthly SSF contribution per employee", fn:()=>downloadSso10Report(parseInt(reportMonth),parseInt(reportYear)) },
              ].map(r=>(
                <div key={r.key} style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>{r.label}</div>
                  <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>{r.desc}</div>
                  <button disabled={reportBusy===r.key} onClick={async()=>{setReportBusy(r.key);try{await r.fn();}catch{alert("Download failed");}finally{setReportBusy(null);}}} style={{ ...outlineBtn, color:"#1e3a8a", borderColor:"#1e3a8a" }}>
                    {reportBusy===r.key?"Generating…":"⬇ CSV"}
                  </button>
                </div>
              ))}
              <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>WHT Certificate</div>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>Annual PDF per employee</div>
                <button disabled={!reportEmp||reportBusy==="wht"} onClick={async()=>{if(!reportEmp)return;setReportBusy("wht");try{await downloadWhtCertificate(reportEmp,parseInt(reportYear));}catch{alert("Download failed");}finally{setReportBusy(null);}}}
                  style={{ ...outlineBtn, color:"#1e3a8a", borderColor:"#1e3a8a", opacity:reportEmp?1:0.5 }}>
                  {reportBusy==="wht"?"Generating…":"⬇ PDF"}
                </button>
                {!reportEmp && <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>Select employee above</div>}
              </div>
              <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>Annual Payroll Summary</div>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>All employees — year {reportYear}</div>
                <button disabled={reportBusy==="ann"} onClick={async()=>{setReportBusy("ann");try{await downloadAnnualSummary(parseInt(reportYear));}catch{alert("Download failed");}finally{setReportBusy(null);}}} style={{ ...outlineBtn, color:"#1e3a8a", borderColor:"#1e3a8a" }}>
                  {reportBusy==="ann"?"Generating…":"⬇ CSV"}
                </button>
              </div>
              <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>Bank Payment File</div>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>Unpaid slips for {MONTHS[parseInt(reportMonth)-1]} {reportYear}</div>
                <button disabled={reportBusy==="bank"} onClick={async()=>{setReportBusy("bank");try{await downloadBankFile(parseInt(reportMonth),parseInt(reportYear),bankFmt);}catch{alert("Download failed");}finally{setReportBusy(null);}}} style={{ ...outlineBtn, color:"#1e3a8a", borderColor:"#1e3a8a" }}>
                  {reportBusy==="bank"?"Generating…":"⬇ CSV"}
                </button>
              </div>
            </div>
          </div>
          )}

          {/* India Exports */}
          {isIN && (
          <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:20 }}>
            <div style={{ fontWeight:700, fontSize:14, color:"#991b1b", marginBottom:14 }}>🇮🇳 India</div>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>Form 16 — Annual TDS</div>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>FY {reportFyYear}-{String(parseInt(reportFyYear)+1).slice(-2)}</div>
                <button disabled={!reportEmp||reportBusy==="f16"} onClick={async()=>{if(!reportEmp)return;setReportBusy("f16");try{await downloadForm16(reportEmp,parseInt(reportFyYear));}catch{alert("Download failed");}finally{setReportBusy(null);}}}
                  style={{ ...outlineBtn, color:"#991b1b", borderColor:"#fecaca", opacity:reportEmp?1:0.5 }}>
                  {reportBusy==="f16"?"Generating…":"⬇ PDF"}
                </button>
                {!reportEmp && <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>Select employee above</div>}
              </div>
              <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>Annual Payroll Summary</div>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>All employees — year {reportYear}</div>
                <button disabled={reportBusy==="ann"} onClick={async()=>{setReportBusy("ann");try{await downloadAnnualSummary(parseInt(reportYear));}catch{alert("Download failed");}finally{setReportBusy(null);}}} style={{ ...outlineBtn, color:"#991b1b", borderColor:"#fecaca" }}>
                  {reportBusy==="ann"?"Generating…":"⬇ CSV"}
                </button>
              </div>
              <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:8, padding:16, minWidth:200 }}>
                <div style={{ fontWeight:600, fontSize:13, color:"#1e3a8a", marginBottom:4 }}>Bank Payment File (NEFT)</div>
                <div style={{ fontSize:12, color:"#64748b", marginBottom:12 }}>Unpaid slips for {MONTHS[parseInt(reportMonth)-1]} {reportYear}</div>
                <button disabled={reportBusy==="bank"} onClick={async()=>{setReportBusy("bank");try{await downloadBankFile(parseInt(reportMonth),parseInt(reportYear),bankFmt);}catch{alert("Download failed");}finally{setReportBusy(null);}}} style={{ ...outlineBtn, color:"#991b1b", borderColor:"#fecaca" }}>
                  {reportBusy==="bank"?"Generating…":"⬇ CSV"}
                </button>
              </div>
            </div>
          </div>
          )}
        </div>
        );
      })()}

      {/* ══ Tab: Salary Structure ══════════════════════════════════════════════ */}
      {activeTab==="structure" && (
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "280px 1fr", gap:20 }}>
          {/* Employee list */}
          <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:16, height:"fit-content", maxHeight: isMobile ? 220 : "none", overflowY: isMobile ? "auto" : "visible" }}>
            <div style={{ fontWeight:600, fontSize:13, color:"#475569", marginBottom:10 }}>Select Employee</div>
            {(employees as any[]).map((e:any)=>(
              <button key={e.id} onClick={()=>loadStructForEmp(e.id)} style={{
                display:"block", width:"100%", textAlign:"left", padding:"8px 12px", borderRadius:6, border:"none", cursor:"pointer", fontSize:13,
                background: structEmp===e.id ? "#eff6ff" : "none",
                color: structEmp===e.id ? "#1e3a8a" : "#334155",
                fontWeight: structEmp===e.id ? 600 : 400,
                marginBottom:2,
              }}>
                <span style={{ textTransform:"capitalize" }}>{e.name}</span>
                {e.has_salary_structure && <span style={{ fontSize:10, color:"#22c55e", marginLeft:6 }}>●</span>}
                {e.basic_salary && <div style={{ fontSize:11, color:"#94a3b8" }}>{e.basic_salary?.toLocaleString()}</div>}
              </button>
            ))}
          </div>
          {/* Structure editor */}
          <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:24 }}>
            {!structEmp ? (
              <div style={{ color:"#94a3b8", fontSize:14, textAlign:"center", padding:"40px 0" }}>Select an employee to set their salary structure</div>
            ) : (
              <>
                <h3 style={{ margin:"0 0 16px", color:"#0f172a" }}>
                  Salary Structure — {(employees as any[]).find((e:any)=>e.id===structEmp)?.name}
                </h3>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr", gap:14, marginBottom:14 }}>
                  <div>
                    <label style={labelSt}>Basic Salary *</label>
                    <input type="number" value={structForm.basic_salary} onChange={e=>setStructForm(f=>({...f,basic_salary:e.target.value}))} style={inputSt} min={1} step="0.01" placeholder="0.00"/>
                    {structTriedSubmit && structErrors.basic_salary && <div style={{ color:"#dc2626", fontSize:12, marginTop:4 }}>{structErrors.basic_salary}</div>}
                  </div>
                  <div>
                    <label style={labelSt}>Currency</label>
                    <select value={structForm.currency} onChange={e=>{const found=(currencies as any[]).find((c:any)=>c.code===e.target.value);setStructForm(f=>({...f,currency:e.target.value,currency_symbol:found?.symbol||""}));}} style={inputSt}>
                      <option value="">Tenant default</option>
                      {(currencies as any[]).map((c:any)=><option key={c.code} value={c.code}>{c.name} ({c.symbol})</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelSt}>Effective Date</label>
                    <DateInput value={structForm.effective_date} onChange={e=>setStructForm(f=>({...f,effective_date:e.target.value}))} style={inputSt}/>
                  </div>
                  <div>
                    <label style={labelSt}>Working Days</label>
                    <input type="number" value={structForm.working_days} onChange={e=>setStructForm(f=>({...f,working_days:e.target.value}))} style={inputSt} min={0} step="0.5" placeholder="Tenant default"/>
                  </div>
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <label style={{ fontWeight:600, fontSize:13, color:"#475569" }}>Fixed Components</label>
                    <button type="button" onClick={addStructComp} style={outlineBtn}>+ Add</button>
                  </div>
                  {structTriedSubmit && structErrors.components && <div style={{ color:"#dc2626", fontSize:12, marginBottom:8 }}>{structErrors.components}</div>}
                  {structForm.components.map((c,i)=>(
                    <div key={i} className="grid-slip-component" style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 130px 120px 36px", gap:8, marginBottom:8 }}>
                      <input placeholder="Name" value={c.name} onChange={e=>updStructComp(i,"name",e.target.value)} style={inputSt}/>
                      <select value={c.type} onChange={e=>updStructComp(i,"type",e.target.value)} style={inputSt}>
                        <option value="earning">Earning</option>
                        <option value="deduction">Deduction</option>
                      </select>
                      <input type="number" placeholder="Amount" value={c.amount} onChange={e=>updStructComp(i,"amount",e.target.value)} style={inputSt} min={0} step="0.01"/>
                      <button type="button" onClick={()=>remStructComp(i)} style={{ background:"none", border:"1px solid #fca5a5", color:"#dc2626", borderRadius:6, cursor:"pointer" }}>✕</button>
                    </div>
                  ))}
                </div>
                <button onClick={saveStructure} disabled={structMut.isPending || Object.keys(structErrors).length > 0} style={primaryBtn}>
                  {structMut.isPending ? "Saving…" : "Save Structure"}
                </button>

                {(structHistory as any[]).length > 1 && (
                  <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid #e2e8f0" }}>
                    <div style={{ fontWeight:600, fontSize:13, color:"#475569", marginBottom:8 }}>Version History</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {(structHistory as any[]).map((v:any)=>(
                        <div key={v.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, padding:"6px 10px", borderRadius:6, background: v.is_current ? "#eff6ff" : "#f8fafc" }}>
                          <span style={{ color:"#334155" }}>
                            {v.currency_symbol}{Number(v.basic_salary).toLocaleString()}
                            {v.effective_date ? ` — effective ${v.effective_date}` : " — no effective date"}
                          </span>
                          <span style={{ color: v.is_current ? "#1d4ed8" : "#94a3b8", fontWeight: v.is_current ? 600 : 400 }}>
                            {v.is_current ? "Current" : "Superseded"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ══ Tab: Slips ════════════════════════════════════════════════════════ */}
      {activeTab==="slips" && (
        <>
          {/* Run Payroll form */}
          {showRunForm && (
            <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:24, marginBottom:24 }}>
              <h3 style={{ margin:"0 0 6px", color:"#0f766e", fontSize:16 }}>Run Payroll</h3>
              <p style={{ margin:"0 0 14px", fontSize:13, color:"#64748b" }}>
                Generates draft slips for all active employees. Uses salary master if set; falls back to last slip as template.
              </p>
              <div style={{ display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap", marginBottom:14 }}>
                <div>
                  <label style={labelSt}>Month</label>
                  <select value={runMonth} onChange={e=>setRunMonth(e.target.value)} style={{ ...inputSt, width:"auto" }}>
                    {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Year</label>
                  <input type="number" value={runYear} onChange={e=>setRunYear(e.target.value)} style={{ ...inputSt, width:90 }} min={2020} max={2099}/>
                </div>
                <button disabled={runMut.isPending} onClick={()=>runMut.mutate({ month:parseInt(runMonth), year:parseInt(runYear), ...runOptions })}
                  style={{ ...primaryBtn, marginBottom:1 }}>
                  {runMut.isPending ? "Running…" : "▶ Run"}
                </button>
              </div>
              <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                  <input type="checkbox" checked={runOptions.apply_statutory} onChange={e=>setRunOptions(o=>({...o,apply_statutory:e.target.checked}))}/>
                  Auto-apply statutory deductions (SSF / PF)
                </label>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                  <input type="checkbox" checked={runOptions.apply_lop} onChange={e=>setRunOptions(o=>({...o,apply_lop:e.target.checked}))}/>
                  Deduct LOP from attendance records
                </label>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                  <input type="checkbox" checked={runOptions.apply_prorate} onChange={e=>setRunOptions(o=>({...o,apply_prorate:e.target.checked}))}/>
                  Pro-rate salary for new joiners
                </label>
              </div>
            </div>
          )}

          {/* Run result */}
          {runResult && (
            <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"14px 20px", marginBottom:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontWeight:700, color:"#166534", fontSize:14, marginBottom:6 }}>
                    ✅ Payroll run complete — {MONTHS[parseInt(runMonth)-1]} {runYear}
                  </div>
                  <div style={{ fontSize:13, color:"#166534" }}>
                    <strong>{runResult.created?.length}</strong> slip{runResult.created?.length!==1?"s":""} created
                    {runResult.skipped?.length>0 && <span style={{ marginLeft:12, color:"#854d0e" }}><strong>{runResult.skipped.length}</strong> skipped</span>}
                    {runResult.no_template?.length>0 && <span style={{ marginLeft:12, color:"#b45309" }}><strong>{runResult.no_template.length}</strong> no salary master</span>}
                    {runResult.errors?.length>0 && <span style={{ marginLeft:12, color:"#dc2626" }}><strong>{runResult.errors.length}</strong> errors</span>}
                  </div>
                  {runResult.no_template?.length>0 && (
                    <div style={{ marginTop:6, fontSize:12, color:"#b45309", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"6px 10px" }}>
                      {/* Named for the sidebar item — the "Structure tab" this pointed at
                          stopped existing when module-subpage-navs made these routes. */}
                      No salary master (set in Salary Structure): {runResult.no_template.map((e:any)=>e.name).join(", ")}
                    </div>
                  )}
                  {runResult.created?.some((c:any)=>c.is_prorated) && (
                    <div style={{ marginTop:4, fontSize:12, color:"#7c3aed" }}>
                      Prorated: {runResult.created.filter((c:any)=>c.is_prorated).map((c:any)=>c.employee).join(", ")}
                    </div>
                  )}
                </div>
                <button onClick={()=>setRunResult(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", fontSize:18 }}>✕</button>
              </div>
              {/* Issue all button */}
              {runResult.created?.length>0 && (
                <button style={{ ...primaryBtn, marginTop:10, fontSize:13, padding:"7px 16px" }}
                  disabled={issueAllMut.isPending}
                  onClick={async()=>{
                    if(await confirm(`Issue all ${runResult.created.length} new slips to employees now?`)){
                      issueAllMut.mutate({month:parseInt(runMonth),year:parseInt(runYear)});
                    }
                  }}>
                  {issueAllMut.isPending ? "Issuing…" : `✓ Issue All ${runResult.created.length} Slips`}
                </button>
              )}
            </div>
          )}

          {/* Bulk issue banner when drafts exist */}
          {!runResult && draftCount>0 && (
            <div style={{ background:"#fef9c3", border:"1px solid #fde047", borderRadius:8, padding:"10px 16px", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:13, color:"#854d0e", fontWeight:500 }}>{draftCount} draft slip{draftCount>1?"s":""} pending issue</span>
              <button style={{ ...primaryBtn, fontSize:12, padding:"6px 14px" }} disabled={issueAllMut.isPending}
                onClick={async()=>{
                  const m = parseInt(runMonth); const y = parseInt(runYear);
                  if(await confirm(`Issue all draft slips for ${MONTHS[m-1]} ${y}?`)){
                    issueAllMut.mutate({month:m,year:y});
                  }
                }}>
                {issueAllMut.isPending ? "Issuing…" : "✓ Issue All Drafts"}
              </button>
            </div>
          )}

          {/* Single slip form */}
          {showForm && (
            <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:24, marginBottom:24 }}>
              <h3 style={{ margin:"0 0 18px", color:"#1e3a8a" }}>
                {editingSlip ? `Edit Slip — ${editingSlip.employee_name} · ${MONTHS[(editingSlip.month||1)-1]} ${editingSlip.year}` : "New Salary Slip"}
              </h3>
              <form onSubmit={handleCreate}>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:14, marginBottom:14 }}>
                  <div>
                    <label style={labelSt}>Employee {!editingSlip && "*"}</label>
                    {editingSlip ? (
                      <input value={(editingSlip.employee_name||"").replace(/\b\w/g,(c:string)=>c.toUpperCase())} style={{ ...inputSt, background:"#f8fafc", color:"#94a3b8" }} disabled/>
                    ) : (
                      <select value={form.user_id} onChange={e=>setForm(f=>({...f,user_id:e.target.value}))} style={inputSt} required>
                        <option value="">Select employee</option>
                        {(employees as any[]).map((e:any)=><option key={e.id} value={e.id}>{(e.name||"").replace(/\b\w/g,(c:string)=>c.toUpperCase())} {e.employee_id?`(${e.employee_id})`:""}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <label style={labelSt}>Month</label>
                    {editingSlip ? (
                      <input value={MONTHS[(editingSlip.month||1)-1]} style={{ ...inputSt, background:"#f8fafc", color:"#94a3b8" }} disabled/>
                    ) : (
                      <select value={form.month} onChange={e=>setForm(f=>({...f,month:e.target.value}))} style={inputSt}>
                        {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <label style={labelSt}>Year</label>
                    <input type="number" value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))}
                      style={editingSlip ? { ...inputSt, background:"#f8fafc", color:"#94a3b8" } : inputSt}
                      disabled={!!editingSlip} min={2020} max={2099}/>
                  </div>
                  <div>
                    <label style={labelSt}>Slip Type</label>
                    <select value={form.slip_type} onChange={e=>setForm(f=>({...f,slip_type:e.target.value}))} style={inputSt}>
                      {SLIP_TYPES.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelSt}>Currency</label>
                    <select value={form.currency} onChange={e=>onCurrencyChange(e.target.value)} style={inputSt}>
                      <option value="">Tenant default</option>
                      {(currencies as any[]).map((c:any)=><option key={c.code} value={c.code}>{c.name} ({c.symbol})</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelSt}>Basic Salary *</label>
                    <input type="number" value={form.basic_salary} onChange={e=>setForm(f=>({...f,basic_salary:e.target.value}))} placeholder="0.00" style={inputSt} required min={0} step="0.01"/>
                  </div>
                  <div>
                    <label style={labelSt}>Worked Days</label>
                    <div style={{ display:"flex", gap:6 }}>
                      <input type="number" value={form.worked_days} onChange={e=>setForm(f=>({...f,worked_days:e.target.value}))} placeholder="e.g. 26" style={{ ...inputSt, flex:1 }} min={0} max={31}/>
                      {form.user_id && !editingSlip && (
                        <button type="button" onClick={handleAutoWorkedDays} disabled={calcWorkedDays} title="Calculate from attendance" style={{ ...outlineBtn, padding:"6px 10px", fontSize:12, whiteSpace:"nowrap", opacity: calcWorkedDays ? 0.6 : 1, cursor: calcWorkedDays ? "not-allowed" : "pointer" }}>{calcWorkedDays ? "Calculating…" : "⟳ Auto"}</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Options row */}
                {!editingSlip && (
                  <div style={{ display:"flex", gap:20, marginBottom:14, flexWrap:"wrap" }}>
                    <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                      <input type="checkbox" checked={form.auto_prorate} onChange={e=>setForm(f=>({...f,auto_prorate:e.target.checked}))}/>
                      Pro-rate for new joiner
                    </label>
                    <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                      <input type="checkbox" checked={form.apply_statutory} onChange={e=>setForm(f=>({...f,apply_statutory:e.target.checked}))}/>
                      Apply SSF / PF
                    </label>
                    {form.user_id && (
                      <button type="button" onClick={handleEstimateTax} disabled={estimatingTax} style={{ ...outlineBtn, fontSize:12, opacity: estimatingTax ? 0.6 : 1, cursor: estimatingTax ? "not-allowed" : "pointer" }}>{estimatingTax ? "Estimating…" : "🧮 Estimate Tax"}</button>
                    )}
                  </div>
                )}

                {/* Components */}
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                    <label style={{ fontWeight:600, fontSize:13, color:"#475569" }}>Earnings & Deductions</label>
                    <button type="button" onClick={addComp} style={outlineBtn}>+ Add Component</button>
                  </div>
                  {form.components.map((c,i)=>(
                    <div key={i} style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 130px 120px 36px", gap:8, marginBottom:8 }}>
                      <input placeholder="Name" value={c.name} onChange={e=>updComp(i,"name",e.target.value)} style={inputSt}/>
                      <select value={c.type} onChange={e=>updComp(i,"type",e.target.value)} style={inputSt}>
                        <option value="earning">Earning</option>
                        <option value="deduction">Deduction</option>
                      </select>
                      <input type="number" placeholder="Amount" value={c.amount} onChange={e=>updComp(i,"amount",e.target.value)} style={inputSt} min={0} step="0.01"/>
                      <button type="button" onClick={()=>remComp(i)} style={{ background:"none", border:"1px solid #fca5a5", color:"#dc2626", borderRadius:6, cursor:"pointer" }}>✕</button>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div style={{ background:"#f8fafc", borderRadius:8, padding:14, marginBottom:16 }}>
                  <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:8, textAlign:"center" }}>
                    {[["Gross", calcGross(), "#1e3a8a"], ["Deductions", calcDed(), "#dc2626"], ["Net Pay", calcNet(), "#166534"]].map(([lbl,val,col])=>(
                      <div key={String(lbl)}>
                        <div style={{ fontSize:11, color:"#64748b", marginBottom:2 }}>{lbl}</div>
                        <div style={{ fontWeight:700, color:String(col), fontSize:lbl==="Net Pay"?16:14 }}>
                          {form.currency_symbol}{(val as number).toLocaleString("en",{minimumFractionDigits:2})}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display:"flex", gap:10 }}>
                  <button type="submit" disabled={createMut.isPending||updateMut.isPending} style={primaryBtn}>
                    {createMut.isPending||updateMut.isPending ? "Saving…" : editingSlip ? "Save Changes" : "Generate Slip"}
                  </button>
                  {editingSlip && <button type="button" onClick={()=>{setShowForm(false);setEditingSlip(null);setForm(blankForm());}} style={outlineBtn}>Cancel</button>}
                </div>
              </form>
            </div>
          )}

          {/* Filter row */}
          <div style={{ marginBottom:14, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <select value={selectedEmployee||""} onChange={e=>setSelectedEmployee(e.target.value?parseInt(e.target.value):null)}
              style={{ ...inputSt, width:"auto" }}>
              <option value="">All employees</option>
              {(employees as any[]).map((e:any)=><option key={e.id} value={e.id}>{(e.name||"").replace(/\b\w/g,(c:string)=>c.toUpperCase())}</option>)}
            </select>
            <div style={{ display:"flex", gap:6 }}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{ ...inputSt, width:180, padding:"7px 10px" }}/>
              {search && <button onClick={()=>setSearch("")} style={{ ...outlineBtn, padding:"6px 10px" }}>✕</button>}
            </div>
            {unpaidOnly && (
              <button onClick={()=>setUnpaidOnly(false)} style={{
                display:"flex", alignItems:"center", gap:6, padding:"6px 12px",
                background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:20,
                color:"#991b1b", fontSize:12, fontWeight:600, cursor:"pointer",
              }}>
                Issued &amp; unpaid only ✕
              </button>
            )}
          </div>

          {isLoading && <div style={{ display:"grid", gap:10 }}><SkeletonRow/><SkeletonRow/><SkeletonRow/></div>}

          {/* Mark-paid modal */}
          {showPayModal && (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ background:"white", borderRadius:12, padding:28, width:380, boxShadow:"0 8px 32px rgba(0,0,0,0.15)" }}>
                <h3 style={{ margin:"0 0 16px", color:"#0f172a" }}>Mark as Paid</h3>
                <div style={{ marginBottom:12 }}>
                  <label style={labelSt}>Payment Date</label>
                  <DateInput value={payForm.payment_date} onChange={e=>setPayForm(f=>({...f,payment_date:e.target.value}))} style={inputSt}/>
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={labelSt}>Reference / Transfer ID (optional)</label>
                  <input type="text" value={payForm.payment_reference} onChange={e=>setPayForm(f=>({...f,payment_reference:e.target.value}))} placeholder="e.g. TT2024-001" style={inputSt}/>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={()=>markPaidMut.mutate({id:showPayModal.id,data:payForm})} disabled={markPaidMut.isPending} style={primaryBtn}>
                    {markPaidMut.isPending ? "Saving…" : "Confirm"}
                  </button>
                  <button onClick={()=>setShowPayModal(null)} style={outlineBtn}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Slips table */}
          {openMenuId!==null && menuPos && (()=>{
            const menuSlip = filteredSlips.find((s:any)=>s.id===openMenuId);
            if (!menuSlip) return null;
            return (
              <div key="backdrop" onClick={()=>{setOpenMenuId(null);setMenuPos(null);}} style={{ position:"fixed", inset:0, zIndex:998 }}>
                <div onClick={e=>e.stopPropagation()} style={{ position:"fixed", top:menuPos.top, right:menuPos.right, zIndex:999, background:"white", border:"1px solid #e2e8f0", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", minWidth:180, padding:"4px 0" }}>
                  <button onClick={()=>{handleDownload(menuSlip);setOpenMenuId(null);setMenuPos(null);}} style={menuItem}>
                    {downloading===menuSlip.id?"Downloading…":"⬇ Download PDF"}
                  </button>
                  {menuSlip.status==="draft" && (
                    <button onClick={()=>{startEdit(menuSlip);setOpenMenuId(null);setMenuPos(null);}} style={menuItem}>✏ Edit</button>
                  )}
                  {menuSlip.status==="draft" && (
                    <button onClick={async ()=>{
                      setOpenMenuId(null);setMenuPos(null);
                      const ok = await confirm(`Issue this salary slip to ${menuSlip.employee_name || "this employee"}? They'll be emailed immediately.`, { title: "Issue slip", confirmLabel: "Issue", danger: false });
                      if (ok) issueMut.mutate(menuSlip.id);
                    }} style={{ ...menuItem, color:"#1d4ed8" }}>✓ Issue</button>
                  )}
                  {menuSlip.status==="issued" && menuSlip.payment_status!=="paid" && (
                    <button onClick={()=>{setShowPayModal(menuSlip);setOpenMenuId(null);setMenuPos(null);}} style={{ ...menuItem, color:"#166534" }}>💳 Mark Paid</button>
                  )}
                  {menuSlip.status==="issued" && (
                    <AsyncButton onClick={async()=>{if(await confirm("Void this issued slip? The employee will no longer see it.")){voidMut.mutate(menuSlip.id);setOpenMenuId(null);setMenuPos(null);}}} style={{ ...menuItem, color:"#d97706" }}>⊘ Void</AsyncButton>
                  )}
                  {menuSlip.status==="draft" && (
                    <AsyncButton onClick={async()=>{if(await confirm("Delete this draft slip?")){deleteMut.mutate(menuSlip.id);setOpenMenuId(null);setMenuPos(null);}}} style={{ ...menuItem, color:"#dc2626" }}>✕ Delete</AsyncButton>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="table-wrap" style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10 }}
               onClick={()=>{setOpenMenuId(null);setMenuPos(null);}}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
                  {[
                    { label:"Employee", field:"employee_name" },
                    { label:"Emp ID", field:"employee_id" },
                    { label:"Pay Period", field:"year_month" },
                    { label:"Type", field:"slip_type" },
                    { label:"Net Pay", field:"net_salary", right:true },
                    { label:"Status", field:"status" },
                    { label:"Payment", field:"payment_status" },
                    { label:"", field:"" },
                  ].map(h=>(
                    <th key={h.label} onClick={h.field?()=>setSort(s=>({field:h.field,dir:s.field===h.field&&s.dir==="asc"?"desc":"asc"})):undefined}
                      style={{ padding:"10px 16px", textAlign:h.right?"right":"left", fontWeight:600, color:"#475569", fontSize:12, whiteSpace:"nowrap", cursor:h.field?"pointer":"default", userSelect:"none" }}>
                      {h.label}{h.field?(sort.field===h.field?(sort.dir==="asc"?" ↑":" ↓"):" ↕"):""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSlips.map((s:any)=>(
                  <tr key={s.id}
                      onClick={()=>{if(s.status==="draft") startEdit(s);}}
                      style={{ borderBottom:"1px solid #f1f5f9", background:"white", cursor:s.status==="draft"?"pointer":"default" }}
                      onMouseEnter={e=>(e.currentTarget.style.background="#f8fafc")}
                      onMouseLeave={e=>(e.currentTarget.style.background="white")}>
                    <td style={{ padding:"12px 16px", fontWeight:600, color:"#1e293b", textTransform:"capitalize" }}>
                      {s.employee_name}
                      {s.generated_at && <div style={{ fontSize:11, color:"#94a3b8", fontWeight:400 }}>{formatDate(s.generated_at, "short", tz)}</div>}
                    </td>
                    <td style={{ padding:"12px 16px", color:"#64748b", fontSize:12 }}>{s.employee_id||<span style={{ color:"#cbd5e1" }}>—</span>}</td>
                    <td style={{ padding:"12px 16px", color:"#1e293b", whiteSpace:"nowrap" }}>
                      {s.month_name} {s.year}
                      {s.is_prorated && <span style={{ fontSize:10, color:"#7c3aed", marginLeft:6 }}>prorated</span>}
                      {s.lop_days>0 && <div style={{ fontSize:11, color:"#dc2626" }}>LOP: {s.lop_days}d</div>}
                    </td>
                    <td style={{ padding:"12px 16px" }}>
                      <TypeChip type={s.slip_type}/>
                    </td>
                    <td style={{ padding:"12px 16px", textAlign:"right", fontWeight:600, color:"#1e3a8a", whiteSpace:"nowrap" }}>
                      {s.currency_symbol}{s.net_salary?.toLocaleString("en",{minimumFractionDigits:2})}
                      <span style={{ fontSize:11, fontWeight:400, color:"#64748b", marginLeft:4 }}>{s.currency}</span>
                    </td>
                    <td style={{ padding:"12px 16px" }}><SlipBadge status={s.status}/></td>
                    <td style={{ padding:"12px 16px" }}>
                      <PayBadge status={s.payment_status}/>
                      {s.payment_date && <div style={{ fontSize:11, color:"#94a3b8" }}>{s.payment_date}</div>}
                    </td>
                    <td style={{ padding:"12px 16px", textAlign:"right" }} onClick={e=>e.stopPropagation()}>
                      <button onClick={e=>{e.stopPropagation();if(openMenuId===s.id){setOpenMenuId(null);setMenuPos(null);}else{const r=(e.currentTarget as HTMLButtonElement).getBoundingClientRect();setMenuPos({top:r.bottom+2,right:window.innerWidth-r.right});setOpenMenuId(s.id);}}}
                        style={{ background:"none", border:"1px solid #e2e8f0", borderRadius:6, padding:"4px 10px", cursor:"pointer", color:"#64748b", fontSize:16, lineHeight:1 }}>⋯</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!isLoading && filteredSlips.length===0 && (
              <div style={{ textAlign:"center", padding:"60px 0", color:"#94a3b8" }}>
                <div style={{ fontSize:14, fontWeight:600, color:"#64748b" }}>No salary slips yet.</div>
                <button onClick={()=>{setShowRunForm(true);setShowForm(false);}} style={{ ...primaryBtn, marginTop:16, display:"inline-block" }}>▶ Run Payroll</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function exportSlipsCsv(slips: any[]) {
  const money = (v: any) => v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2);
  const headers = ["Employee","Month","Year","Type","Basic","Gross","Deductions","Net","Currency","Status","Payment","Paid Date"];
  const rows = slips.map(s=>[s.employee_name||"",s.month_name||s.month,s.year,s.slip_type||"regular",
    money(s.basic_salary),money(s.gross_salary),money(s.total_deductions),money(s.net_salary),s.currency||"",s.status||"",s.payment_status||"",s.payment_date||""]);
  const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
  // Leading BOM (bug-17) — without it, Excel opens this UTF-8 file as Windows-1252 and
  // renders any non-ASCII character (e.g. the em dash used for a blank field) as mojibake.
  const blob = new Blob(["\uFEFF" + csv],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download="salary_slips.csv"; a.click();
  URL.revokeObjectURL(url);
}
